// store/slices/sellDeviceSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../services/api";
import { markLeadCompleted } from "../../utils/apiAttribution";
import { trackConfirmedMetaLead } from "../../utils/metaPixel";

export const validateEvaluation = createAsyncThunk(
  "sellDevice/validateEvaluation",
  async ({brandSlug, productSlug, variationSlug }, { rejectWithValue }) => {
    try {
      const response = await api.validateEvaluation({
        brand_slug: brandSlug,
        product_slug: productSlug,
        variation_slug: variationSlug,
      });

      if (response.success === true) {
        return response.data;
      } else {
        return rejectWithValue(response.message || "Invalid evaluation");
      }
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to validate evaluation",
      );
    }
  },
);

// Create service request - only needs variationSlug
export const createServiceRequest = createAsyncThunk(
  "sellDevice/createServiceRequest",
  async (payload, { rejectWithValue }) => {
    try {
      // The API expects a nested location object. Accept the form's `location`
      // as well as the old flat latitude/longitude thunk arguments so neither
      // calling style silently loses coordinates. Zero is a valid coordinate.
      const latitude = payload.location?.latitude ?? payload.latitude;
      const longitude = payload.location?.longitude ?? payload.longitude;
      const location = latitude != null || longitude != null
        ? { latitude: latitude ?? null, longitude: longitude ?? null }
        : undefined;

      const requestPayload = {
        brand_slug: payload.brandSlug,
        product_slug: payload.productSlug,
        variation_slug: payload.variationSlug,
        address_id: payload.addressId,
        consent_accepted: payload.consentAccepted === true,
        location,
        alternate_mobile: payload.alternateMobile,
        is_express_pickup: payload.isExpressPickup || false,
        slot: payload.slot,
        opd: payload.opd,
        spd: payload.spd,
      };

      const response = await api.createServiceRequest(requestPayload);
      if (response.success === true) {
        const confirmed = response.data?.data || response.data || {};
        trackConfirmedMetaLead(confirmed.service_id ?? confirmed.id, confirmed.service_no);
        markLeadCompleted();
        return response.data;
      }
      return rejectWithValue(
        response.message || "Failed to create service request",
      );
    } catch (err) {
      const error = err.response?.data || {};
      return rejectWithValue({
        message: error.message || "Failed to create service request",
        code: error.error_code || error.code,
        downloadAppUrl: error.errors?.download_app_url || null,
        scopeRejected: Boolean(error.errors?.campaign_scope),
      });
    }
  },
);

// Get pincode address
export const getPincodeAddress = createAsyncThunk(
  "sellDevice/getPincodeAddress",
  async (pincode, { rejectWithValue }) => {
    try {
      const response = await api.getPincodeAddress(pincode);
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue("Pincode not serviceable");
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch pincode details");
    }
  },
);

// Get user addresses
export const getUserAddresses = createAsyncThunk(
  "sellDevice/getUserAddresses",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getUserAddresses();
      if (response.success === true) {
        return response.data;
      }
      return rejectWithValue(response.message || "Failed to fetch addresses");
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch addresses",
      );
    }
  },
);

// Save new address
export const saveAddress = createAsyncThunk(
  "sellDevice/saveAddress",
  async (addressData, { rejectWithValue }) => {
    try {
      const response = await api.saveAddress(addressData);
      if (response.success === true) {
        return response.data;
      }
      return rejectWithValue(response.message || "Failed to save address");
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to save address",
      );
    }
  },
);

// Edit address
export const editAddress = createAsyncThunk(
  "sellDevice/editAddress",
  async ({ addressId, addressData }, { rejectWithValue }) => {
    try {
      const response = await api.editAddress(addressId, addressData);
      if (response.success === true) {
        return { id: addressId, data: response.data };
      }
      return rejectWithValue(response.message || "Failed to update address");
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update address",
      );
    }
  },
);

// Delete address
export const deleteAddress = createAsyncThunk(
  "sellDevice/deleteAddress",
  async (addressId, { rejectWithValue }) => {
    try {
      const response = await api.deleteAddress(addressId);
      if (response.success === true) {
        return addressId;
      }
      return rejectWithValue(response.message || "Failed to delete address");
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to delete address",
      );
    }
  },
);

// ============ COUPON ACTIONS ============
// Apply coupon - uses variationSlug from params
export const applyCouponToEvaluation = createAsyncThunk(
  "sellDevice/applyCoupon",
  async ({brandSlug, productSlug, variationSlug, couponCode }, { rejectWithValue }) => {
    try {
      const response = await api.applyCoupon(brandSlug, productSlug, variationSlug, couponCode);
      if (response.success === true) {
        return response.data;
      }
      return rejectWithValue(response.message || "Failed to apply coupon");
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to apply coupon",
      );
    }
  },
);

// Remove coupon - uses variationSlug
export const removeCouponFromEvaluation = createAsyncThunk(
  "sellDevice/removeCoupon",
  async ({brandSlug, productSlug, variationSlug}, { rejectWithValue }) => {
    try {
      const response = await api.removeCoupon(brandSlug, productSlug, variationSlug);
      if (response.success === true) {
        return response.data;
      }
      return rejectWithValue(response.message || "Failed to remove coupon");
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to remove coupon",
      );
    }
  },
);

const initialState = {
  evaluationValid: false,
  evaluationResponse: null,
  evaluationError: null,
  productData: null,
  pincodeData: null,
  pincodeLoading: false,
  areas: [],
  pincodeUnavailable: false,
  loading: false,
  submitting: false,
  validationLoading: false,
  addresses: [],
  addressesLoading: false,
  addressesError: null,
  savingAddress: false,
  editingAddress: false,
  deletingAddress: false,
  selectedAddressId: null,
  // Coupon states
  couponApplied: false,
  couponCode: null,
  couponBonusAmount: 0,
  couponBonusType: null,
  couponError: null,
  applyingCoupon: false,
  removingCoupon: false,
};

const sellDeviceSlice = createSlice({
  name: "sellDevice",
  initialState,
  reducers: {
    clearSellDevice: (state) => {
      Object.assign(state, initialState);
    },
    clearPincodeData: (state) => {
      state.pincodeData = null;
      state.areas = [];
      state.pincodeUnavailable = false;
    },
    setPincodeLoading: (state, action) => {
      state.pincodeLoading = action.payload;
    },
    setSelectedAddress: (state, action) => {
      state.selectedAddressId = action.payload;
    },
    setPincodeUnavailable: (state, action) => {
      state.pincodeUnavailable = action.payload;
    },
    clearAddresses: (state) => {
      state.addresses = [];
      state.addressesError = null;
      state.selectedAddressId = null;
    },
    // Clear coupon states
    clearCoupon: (state) => {
      state.couponApplied = false;
      state.couponCode = null;
      state.couponBonusAmount = 0;
      state.couponBonusType = null;
      state.couponError = null;
      state.applyingCoupon = false;
      state.removingCoupon = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validateEvaluation.fulfilled, (state, action) => {
        state.validationLoading = false;
        state.evaluationValid = true;
        state.evaluationResponse = action.payload;
        state.evaluationError = null;

        const data = action.payload;
        if (data) {
          // Handle nested coupon object from API response
          const couponData = data.coupon || {};

          state.productData = {
            id: data.id,
            evaluated_amount: data.evaluated_amount,
            variation: data.variation || null,
            product: data.variation?.product || null,
            // Include coupon data if present
            coupon_code: couponData.code || null,
            coupon_bonus_amount: couponData.bonus_amount || 0,
            coupon_bonus_type: couponData.bonus_type || null,
            has_coupon_applied: couponData.is_applied || false,
          };

          // Update coupon states from response
          if (couponData.is_applied) {
            state.couponApplied = true;
            state.couponCode = couponData.code;
            state.couponBonusAmount = couponData.bonus_amount || 0;
            state.couponBonusType = couponData.bonus_type;
          } else {
            state.couponApplied = false;
            state.couponCode = null;
            state.couponBonusAmount = 0;
            state.couponBonusType = null;
          }
        }
      })
      .addCase(validateEvaluation.rejected, (state, action) => {
        state.validationLoading = false;
        state.evaluationValid = false;
        state.evaluationError = action.payload || "Validation failed";
        state.evaluationResponse = null;
        state.productData = null;
      })
      // Get pincode address
      .addCase(getPincodeAddress.pending, (state) => {
        state.pincodeLoading = true;
        state.pincodeUnavailable = false;
      })
      .addCase(getPincodeAddress.fulfilled, (state, action) => {
        state.pincodeLoading = false;
        state.pincodeData = action.payload;
        state.areas = action.payload.areas || [];
        state.pincodeUnavailable = !(action.payload.areas?.length > 0);
      })
      .addCase(getPincodeAddress.rejected, (state) => {
        state.pincodeLoading = false;
        state.pincodeData = null;
        state.areas = [];
        state.pincodeUnavailable = true;
      })
      // Get user addresses
      .addCase(getUserAddresses.pending, (state) => {
        state.addressesLoading = true;
        state.addressesError = null;
      })
      .addCase(getUserAddresses.fulfilled, (state, action) => {
        state.addressesLoading = false;
        state.addresses = Array.isArray(action.payload) ? action.payload : [];
        state.addressesError = null;
      })
      .addCase(getUserAddresses.rejected, (state, action) => {
        state.addressesLoading = false;
        state.addresses = [];
        state.addressesError = action.payload || "Failed to fetch addresses";
      })
      // Save address
      .addCase(saveAddress.pending, (state) => {
        state.savingAddress = true;
      })
      .addCase(saveAddress.fulfilled, (state, action) => {
        state.savingAddress = false;
        state.addresses = [action.payload, ...state.addresses];
      })
      .addCase(saveAddress.rejected, (state) => {
        state.savingAddress = false;
      })
      // Edit address
      .addCase(editAddress.pending, (state) => {
        state.editingAddress = true;
      })
      .addCase(editAddress.fulfilled, (state, action) => {
        state.editingAddress = false;
        const index = state.addresses.findIndex(
          (addr) => addr.id === action.payload.id,
        );
        if (index !== -1) {
          state.addresses[index] = {
            ...state.addresses[index],
            ...action.payload.data,
          };
        }
      })
      .addCase(editAddress.rejected, (state) => {
        state.editingAddress = false;
      })
      // Delete address
      .addCase(deleteAddress.pending, (state) => {
        state.deletingAddress = true;
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.deletingAddress = false;
        state.addresses = state.addresses.filter(
          (addr) => addr.id !== action.payload,
        );
        if (state.selectedAddressId === action.payload) {
          state.selectedAddressId = null;
        }
      })
      .addCase(deleteAddress.rejected, (state) => {
        state.deletingAddress = false;
      })
      // Create service request
      .addCase(createServiceRequest.pending, (state) => {
        state.submitting = true;
      })
      .addCase(createServiceRequest.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(createServiceRequest.rejected, (state) => {
        state.submitting = false;
      })
      // ============ COUPON CASES ============
      // Apply coupon
      .addCase(applyCouponToEvaluation.pending, (state) => {
        state.applyingCoupon = true;
        state.couponError = null;
      })
      .addCase(applyCouponToEvaluation.fulfilled, (state, action) => {
        state.applyingCoupon = false;

        // Handle nested coupon object from API response
        const responseData = action.payload;
        const couponData = responseData.coupon || {};

        state.couponApplied = couponData.is_applied || false;
        state.couponCode = couponData.code || null;
        state.couponBonusAmount = couponData.bonus_amount || 0;
        state.couponBonusType = couponData.bonus_type || null;
        state.couponError = null;

        // Update evaluation data with new amount
        if (state.evaluationResponse) {
          state.evaluationResponse = {
            ...state.evaluationResponse,
            ...responseData,
            coupon_code: couponData.code,
            coupon_bonus_amount: couponData.bonus_amount,
            coupon_bonus_type: couponData.bonus_type,
            has_coupon_applied: couponData.is_applied,
          };
        }
        // Update product data
        if (state.productData) {
          state.productData = {
            ...state.productData,
            evaluated_amount:
              responseData.evaluated_amount ||
              state.productData.evaluated_amount,
            coupon_bonus_amount: couponData.bonus_amount || 0,
            coupon_code: couponData.code || null,
            coupon_bonus_type: couponData.bonus_type || null,
            has_coupon_applied: couponData.is_applied || false,
          };
        }
      })
      .addCase(applyCouponToEvaluation.rejected, (state, action) => {
        state.applyingCoupon = false;
        state.couponError = action.payload || "Failed to apply coupon";
        state.couponApplied = false;
      })
      .addCase(removeCouponFromEvaluation.pending, (state) => {
        state.removingCoupon = true;
        state.couponError = null;
      })
      .addCase(removeCouponFromEvaluation.fulfilled, (state, action) => {
        state.removingCoupon = false;

        const responseData = action.payload;
        const couponData = responseData.coupon || {};

        state.couponApplied = couponData.is_applied || false;
        state.couponCode = couponData.code || null;
        state.couponBonusAmount = couponData.bonus_amount || 0;
        state.couponBonusType = couponData.bonus_type || null;
        state.couponError = null;

        // Update evaluation data
        if (state.evaluationResponse) {
          state.evaluationResponse = {
            ...state.evaluationResponse,
            ...responseData,
            coupon_code: couponData.code,
            coupon_bonus_amount: couponData.bonus_amount,
            coupon_bonus_type: couponData.bonus_type,
            has_coupon_applied: couponData.is_applied,
          };
        }
        if (state.productData) {
          state.productData = {
            ...state.productData,
            evaluated_amount:
              responseData.evaluated_amount ||
              state.productData.evaluated_amount,
            coupon_bonus_amount: couponData.bonus_amount || 0,
            coupon_code: couponData.code || null,
            coupon_bonus_type: couponData.bonus_type || null,
            has_coupon_applied: couponData.is_applied || false,
          };
        }
      })
      .addCase(removeCouponFromEvaluation.rejected, (state, action) => {
        state.removingCoupon = false;
        state.couponError = action.payload || "Failed to remove coupon";
      });
  },
});

export const {
  clearSellDevice,
  clearPincodeData,
  setPincodeLoading,
  setSelectedAddress,
  setPincodeUnavailable,
  clearAddresses,
  clearCoupon,
} = sellDeviceSlice.actions;

export default sellDeviceSlice.reducer;
